<script setup lang="ts">
import { Ref, computed, nextTick, ref, watch } from 'vue';
import type { AxiosError } from 'axios';
import { ValidationObserver, ValidationProvider } from 'vee-validate';
import { useStore } from '@/store';
import { useToast } from '@/utils/bootstrapVueUtils';
import {
    getAppStoreInstallUrl,
    isAppStoreType,
    requiresStoreSubscription,
    sharesSubscriptionByType,
    storeTypeOptions
} from '@/utils/stores';
import { addStore, createStoreCheckout } from '@/api/customily/stores';
import CModal from '@/components/shared/CModal.vue';
import CSelect, { type CSelectItem } from '@/components/shared/CSelect';
import CInput from '@/components/shared/CInput.vue';
import CButton from '@/components/shared/CButton/CButton.vue';
import CInfo from '@/components/shared/CInfo.vue';
import CError from '@/components/shared/CError.vue';
import { StoreType, type Store } from '@extTypes/api/customily/user';

const DEFAULT_STORE_TYPE = storeTypeOptions.find((o) => o.value === StoreType.Shopify)!;
const SUBSCRIPTION_MONTHLY_PRICE = 49;

const props = defineProps<{
    value: boolean;
}>();

const emit = defineEmits<{
    (event: 'input', value: boolean): void;
    (event: 'added', store: Store): void;
}>();

const { state, commit } = useStore();
const bvToast = useToast();

const formRef: Ref<InstanceType<typeof ValidationObserver> | null> = ref(null);
const storeTypeOption: Ref<CSelectItem<StoreType>> = ref(DEFAULT_STORE_TYPE);
const storeUrl = ref('');
const saving = ref(false);
const errorMessage = ref('');

const requiresSubscription = computed(() =>
    requiresStoreSubscription(storeTypeOption.value.value, state.user?.customilyUser.Stores || [])
);

const urlField = computed(() => {
    switch (storeTypeOption.value.value) {
        case StoreType.Shopify: {
            return { label: 'Store URL', placeholder: 'YourStore.myshopify.com', rules: 'required|shopify-url' };
        }
        case StoreType.Shoplazza: {
            return { label: 'Store URL', placeholder: 'YourStore.myshoplaza.com', rules: 'required|shoplazza-url' };
        }
        case StoreType.Etsy: {
            return { label: 'Store URL', placeholder: 'etsy.com/shop/YourStore', rules: 'required|url' };
        }
        case StoreType.Amazon: {
            return { label: 'Store Name', placeholder: 'YourStoreName', rules: 'required|alpha_num' };
        }
        default: {
            return { label: 'Store URL', placeholder: 'YourStore.com', rules: 'required|url' };
        }
    }
});

const isFormInvalid = computed(() => formRef.value?.flags.invalid ?? true);

const submitText = computed(() => {
    if (saving.value) return 'Adding Store...';
    return requiresSubscription.value ? 'Subscribe & Add Store' : 'Add Store';
});

watch(
    () => props.value,
    async (isOpen) => {
        if (!isOpen) return;

        storeTypeOption.value = DEFAULT_STORE_TYPE;
        storeUrl.value = '';
        errorMessage.value = '';

        // The modal content is kept between openings, clear the validation state of the previous one
        await nextTick();
        formRef.value?.reset();
    }
);

const close = () => emit('input', false);

const toast = (message: string, variant: 'success' | 'danger' = 'success') => {
    bvToast?.toast(message, { title: 'Add Store', variant, solid: true });
};

// The app store install adds the store to the user, and bills it
const installFromAppStore = (type: StoreType) => {
    window.open(getAppStoreInstallUrl(type, storeUrl.value), '_blank', 'noopener');
    close();
    toast('Complete the Customily installation in the new tab to add the store.');
};

const addStoreAndSubscribe = async (type: StoreType) => {
    // Opened during the click because popup blockers reject the windows opened after an await
    const checkoutWindow = requiresSubscription.value ? window.open('', '_blank') : null;
    if (checkoutWindow) checkoutWindow.opener = null;

    saving.value = true;
    errorMessage.value = '';
    let addedStore: Store | null = null;

    try {
        const result = await addStore(storeUrl.value, type);
        addedStore = result.store;
        commit('addUserStore', addedStore);
        emit('added', addedStore);

        if (result.requiresSubscription) {
            const checkoutUrl = await createStoreCheckout(addedStore.Url);
            if (checkoutWindow) {
                checkoutWindow.location.href = checkoutUrl;
            } else {
                window.open(checkoutUrl, '_blank', 'noopener');
            }
        } else {
            checkoutWindow?.close();
        }

        close();
        toast(
            result.requiresSubscription
                ? `${addedStore.Url} was added. Complete the subscription in the new tab to activate it.`
                : `${addedStore.Url} was added.`
        );
    } catch (error_) {
        checkoutWindow?.close();
        const error = error_ as AxiosError<{ Message?: string }>;

        // The store is already added, only its checkout failed
        if (addedStore) {
            window.captureException(error);
            close();
            toast(`${addedStore.Url} was added, but the subscription checkout could not be opened.`, 'danger');
        } else if (error.response?.status === 400 && error.response.data?.Message) {
            errorMessage.value = error.response.data.Message;
        } else {
            errorMessage.value = 'Error adding the store, please try again.';
            window.captureException(error);
        }
    } finally {
        saving.value = false;
    }
};

const onSubmit = () => {
    if (isFormInvalid.value || saving.value) return;

    const type = storeTypeOption.value.value;
    if (isAppStoreType(type)) {
        installFromAppStore(type);
        return;
    }
    addStoreAndSubscribe(type);
};
</script>

<template>
    <c-modal :value="value" title="Add Store" content-class="add-store-modal" @input="emit('input', $event)">
        <validation-observer ref="formRef" tag="form" class="add-store-form" @submit.prevent="onSubmit">
            <c-select
                v-model="storeTypeOption"
                label="Choose a Store Platform"
                label-class="add-store-label"
                :options="storeTypeOptions"
                :clearable="false"
                :disabled="saving"
            />
            <validation-provider
                v-slot="{ valid, failed, errors }"
                class="d-block"
                :name="urlField.label"
                :rules="urlField.rules"
            >
                <c-input
                    v-model="storeUrl"
                    type="text"
                    label-class="add-store-label"
                    :label="urlField.label"
                    :placeholder="urlField.placeholder"
                    :input-class="{ 'is-valid': valid && !!storeUrl }"
                    :disabled="saving"
                    :error="failed"
                    :error-msg="errors[0]"
                />
            </validation-provider>
            <c-info v-if="requiresSubscription" variant="neutral" class="subscription-info">
                <font-awesome-icon :icon="['fas', 'circle-info']" class="mr-3" />
                <span class="flex-grow-1">
                    You need a {{ storeTypeOption.label }} subscription to connect this store.
                    <template v-if="sharesSubscriptionByType(storeTypeOption.value)">
                        One subscription will cover all your {{ storeTypeOption.label }} stores.
                    </template>
                </span>
                <span class="ml-3 text-nowrap">
                    $<b>{{ SUBSCRIPTION_MONTHLY_PRICE }}</b>
                    /mo
                </span>
            </c-info>
            <c-error v-if="errorMessage" :message="errorMessage" />
        </validation-observer>
        <template #modal-footer>
            <c-button text="Cancel" variant="outline-empty" :disabled="saving" @click="close" />
            <c-button variant="primary" :text="submitText" :disabled="isFormInvalid || saving" @click="onSubmit" />
        </template>
    </c-modal>
</template>

<style lang="scss" scoped>
.add-store-form {
    display: flex;
    flex-direction: column;
    gap: 0.875rem;
}

.subscription-info {
    font-size: 0.875rem;
}
</style>

<style lang="scss">
@import '@/assets/css/utilities/functions';

/* Not scoped: bootstrap-vue renders the modal in the body and the labels and inputs belong to child components */
.add-store-modal {
    color: get-color('gray-dark');

    .modal-header {
        padding: 0.625rem 1rem;
        border-bottom-color: get-color('gray-light');
    }

    .modal-title {
        font-size: 1.0625rem;
    }

    .modal-body {
        padding: 1.25rem 1rem;

        // Specific enough to override the label size of CSelect and CInput
        label.add-store-label {
            font-size: 0.75rem;
        }
    }

    .modal-footer {
        padding: 0.5rem 1rem;
        border-top-color: get-color('gray-light');
    }

    .form-control.is-valid {
        border-color: get-color('success');
    }
}
.modal-dialog-scrollable .modal-content.add-store-modal {
    overflow: visible;
    .modal-body {
        overflow: visible;
    }
}
</style>
